import React, { useEffect, useLayoutEffect, useState, startTransition } from 'react'
import { debounce } from 'throttle-debounce'
import logger from 'logger'
import { useAb } from 'hooks'
import { constants } from 'helpers'
import { getCloudinaryImageUrl, isCloudinaryUrl } from 'cloudinary'


// ATTN helps to render faster, but can cause high memory consumption in a browser
const cache = new Set<string>()

const preloadImage = (src: string) => {
  if (cache.has(src)) {
    return Promise.resolve()
  }

  return new Promise<void>((resolve, reject) => {
    const image = new window.Image()
    image.onload = () => {
      cache.add(src)

      if (image.decode) {
        image.decode().then(resolve).catch(reject)
      }
      else {
        resolve()
      }
    }
    image.onerror = reject
    image.src = src
  })
}

type ImageSrcProps = {
  src: string
  bgColor?: string
  width?: number
  height?: number
  quality?: number
  isCloudinaryEnabled?: boolean
}

export const getZinaImage = ({ src, bgColor, quality, width, height, isCloudinaryEnabled }: ImageSrcProps): string => {
  const multiplier = (typeof window !== 'undefined' ? window.devicePixelRatio : 1) || 1
  const resizeValue = Math.ceil((width || height) * multiplier)

  if (!src) {
    return src
  }

  if (isCloudinaryUrl(src) || isCloudinaryEnabled && src.match(/^https?:\/\//)) {
    return getCloudinaryImageUrl(src, {
      [width ? 'width' : 'height']: resizeValue,
      bgColor,
      quality,
    })
  }

  let finalSrc = src
  const params = []

  if (resizeValue > 0) {
    params.push(`${width ? 'w' : 'h'}=${resizeValue}`)
  }

  if (quality) {
    params.push(`q=${quality}`)
  }

  if (bgColor && src.match(/^http(.*?)\.png$/)) {
    finalSrc = finalSrc.replace('.png', '.jpg')
    params.push(`bgcolor=${bgColor.replace('#', '')}`)
  }

  if (!params.length) {
    return finalSrc
  }

  return `${finalSrc}?${params.join('&')}`
}

// track node size and require it
type UseZinaImageProps = Omit<ImageSrcProps, 'width' | 'height'> & {
  nodeRef?: React.RefObject<HTMLElement>
  skip?: boolean
  aspect?: number
  preload?: boolean
  withLoader?: boolean
}

type UseZinaImageState = {
  zinaSrc: string
  originalSrc: string
  isLoading: boolean
  error?: any
}

const useIsomorphicEffect = __CLIENT__ ? useLayoutEffect : useEffect

export const useZinaImage = (props: UseZinaImageProps) => {
  const {
    nodeRef,
    skip,
    preload = true,
    withLoader = false,
    src,
    bgColor,
    aspect,
    quality,
  } = props

  const isCloudinaryEnabled = useAb(constants.abTests.cloudinary) === 'b'
  const placeholder = getPlaceholder(aspect)

  const [ state, setState ] = useState<UseZinaImageState>({
    zinaSrc: placeholder,
    originalSrc: null,
    isLoading: withLoader,
    error: null,
  })

  useIsomorphicEffect(() => {
    const node = nodeRef?.current

    if (!node || skip || !src) {
      return
    }

    let isMounted = true
    let lastImageId = 0
    let lastWidth = 0

    const update = () => {
      if (!isMounted) {
        return
      }

      const width = node.clientWidth
      const height = node.clientHeight

      if (!width && !height) {
        return
      }

      if (lastWidth >= width) {
        return
      }

      lastWidth = width

      const zinaSrc = getZinaImage({
        src,
        bgColor,
        width,
        height,
        quality,
        isCloudinaryEnabled,
      })

      const imageId = ++lastImageId

      if (preload) {
        startTransition(() => {
          setState((prevState) => {
            return {
              // keep previous image size or reset it on src change
              zinaSrc: prevState.originalSrc === src ? prevState.zinaSrc : placeholder,
              originalSrc: src,
              isLoading: withLoader,
              error: null,
            }
          })
        })

        preloadImage(zinaSrc)
          .then(() => {
            if (isMounted && imageId === lastImageId) {
              startTransition(() => {
                setState({ zinaSrc, originalSrc: src, isLoading: false, error: null })
              })
            }
          })
          .catch((error) => {
            if (isMounted) {
              startTransition(() => {
                setState({ zinaSrc: src, originalSrc: src, isLoading: false, error: error })
              })
            }
          })
      }
      else {
        startTransition(() => {
          setState({ zinaSrc, originalSrc: src, isLoading: false, error: null })
        })
      }
    }

    const debouncedUpdate = debounce(() => update(), 100)

    update()
    // ATTN we do debounced update to fix some issues with carousels
    debouncedUpdate()

    let resizeObserver
    try {
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(debouncedUpdate)
        resizeObserver.observe(node)
      }
    }
    catch (error) {
      logger.warn(error)
    }

    return () => {
      isMounted = false

      if (resizeObserver) {
        resizeObserver.unobserve(node)
        resizeObserver.disconnect()
        debouncedUpdate.cancel()
      }
    }
  }, [ nodeRef, skip, preload, src, bgColor, placeholder, quality, withLoader, isCloudinaryEnabled ])

  return state
}

export const getPlaceholder = (aspect = 1) => `data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${aspect} 1'%3e%3c/svg%3e`

